import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { getTimeSlotFromDateString } from "@src/appV2/redesign/DailyView/utils/groupShiftsByTimeSlot";
import { type QueryClient, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

import { getPrnShiftsResponseSchema, type PrnShiftPosting, unwrapPrnShiftListItem } from "../types";

const PRN_SHIFTS_BASE_KEY = "prn-shifts";

interface UseActivePrnShiftPostingsParams {
  workplaceId: string;
  /** Workplace timezone. Hour-based fallback for the AM/PM/NOC bucket when BE's `timeSlot` is a workplace-custom value outside the FE-recognized enum. */
  timezone?: string;
  // ISO-string day-window — when provided, only postings for shifts
  // starting in that range are returned. Maps to the BE's JSON:API filter
  // `?filter[start][gte]=...&filter[start][lte]=...`.
  startIso?: string;
  endIso?: string;
}

interface UseActivePrnShiftPostingsOptions {
  enabled?: boolean;
}

const queryParamsSchema = z.object({
  workplaceId: z.string(),
  "filter[start][gte]": z.string().optional(),
  "filter[start][lte]": z.string().optional(),
});

export function getActivePrnShiftPostingsQueryKey(
  params: UseActivePrnShiftPostingsParams
): readonly unknown[] {
  return [PRN_SHIFTS_BASE_KEY, params.workplaceId, params.startIso, params.endIso] as const;
}

export async function invalidateActivePrnShiftPostingsQuery(
  queryClient: QueryClient,
  workplaceId: string
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: [PRN_SHIFTS_BASE_KEY, workplaceId],
  });
}

// Fetches the workplace's active PRN shift postings (BE PR ALCH-5934 /
// #24798). Returns one row per `groupId` — sibling invites are collapsed
// server-side. A posting stays in this list while an invite is
// PENDING-not-expired or ACCEPTED. After on_expiry auto-release the invites
// stay claimable with the marketplace `shiftId` stamped, so the row remains
// (`shiftId` set, `status` ACTIVE) until they finally expire at shift start.
export function useActivePrnShiftPostings(
  params: UseActivePrnShiftPostingsParams,
  options: UseActivePrnShiftPostingsOptions = {}
): UseQueryResult<PrnShiftPosting[]> {
  const { workplaceId, timezone, startIso, endIso } = params;
  const { enabled = true } = options;

  return useQuery({
    queryKey: getActivePrnShiftPostingsQueryKey(params),
    enabled: enabled && Boolean(workplaceId),
    queryFn: async () => {
      const response = await get({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/prn-shifts`,
        queryParams: {
          workplaceId,
          ...(isDefined(startIso) && { "filter[start][gte]": startIso }),
          ...(isDefined(endIso) && { "filter[start][lte]": endIso }),
        },
        queryParamsSchema,
        responseSchema: getPrnShiftsResponseSchema,
      });

      return response.data.shifts.map((item) =>
        unwrapPrnShiftListItem(item, workplaceId, getTimeSlotFromDateString(item.start, timezone))
      );
    },
    refetchInterval: 60_000,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_PRN_SHIFT_POSTINGS_FAILURE,
    },
  });
}
